import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { authFetch } from "@/lib/api";

const API = "";
const PUBLIC_PATHS = new Set(["/", "/login", "/auth/callback", "/auth-test"]);

const WorkspaceContext = createContext(null);

export function WorkspaceProvider({ children }) {
  const [scope, setScope] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [setupState, setSetupState] = useState(null);
  const [setupLoading, setSetupLoading] = useState(false);

  const shouldSkipBootstrap = useCallback(() => {
    return PUBLIC_PATHS.has(window.location.pathname);
  }, []);

  const fetchScope = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/api/workspaces/me/scope`);
      if (res.ok) {
        const data = await res.json();
        setScope(data);
        return data;
      }
    } catch {}
    return null;
  }, []);

  const fetchWorkspaces = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/api/workspaces`);
      if (res.ok) {
        const data = await res.json();
        setWorkspaces(data.workspaces || []);
      }
    } catch {}
  }, []);

  const fetchAccounts = useCallback(async (workspaceId) => {
    if (!workspaceId) return;
    try {
      const res = await authFetch(`${API}/api/workspaces/${workspaceId}/accounts`);
      if (res.ok) {
        const data = await res.json();
        setAccounts(data.accounts || []);
      }
    } catch {}
  }, []);

  const fetchSetupState = useCallback(async () => {
    setSetupLoading(true);
    try {
      const res = await authFetch(`${API}/api/onboarding/first-run`);
      if (res.ok) {
        const data = await res.json();
        setSetupState(data);
        return data;
      }
    } catch {}
    finally {
      setSetupLoading(false);
    }
    return null;
  }, []);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    const nextScope = await fetchScope();
    if (nextScope) {
      await Promise.all([
        fetchWorkspaces(),
        fetchAccounts(nextScope.workspace_id),
      ]);
    } else {
      setWorkspaces([]);
      setAccounts([]);
      setSetupState(null);
    }
    const nextSetupState = nextScope ? await fetchSetupState() : null;
    setLoading(false);
    return { scope: nextScope, setupState: nextSetupState };
  }, [fetchAccounts, fetchScope, fetchSetupState, fetchWorkspaces]);

  useEffect(() => {
    const init = async () => {
      if (shouldSkipBootstrap()) {
        setScope(null);
        setWorkspaces([]);
        setAccounts([]);
        setSetupState(null);
        setLoading(false);
        return;
      }
      await refreshAll();
    };
    init();
  }, [refreshAll, shouldSkipBootstrap]);

  const switchAccount = useCallback(async (accountId) => {
    if (!scope) return;
    const acct = accounts.find((a) => a.account_id === accountId);
    if (acct) {
      try {
        const res = await authFetch(`${API}/api/workspaces/me/switch-account`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ account_id: accountId }),
        });
        if (!res.ok) return null;
        const nextScope = await fetchScope();
        await fetchAccounts(nextScope?.workspace_id || scope.workspace_id);
        await fetchSetupState();
        return await res.json();
      } catch {}
    }
    return null;
  }, [scope, accounts, fetchAccounts, fetchScope, fetchSetupState]);

  const switchWorkspace = useCallback(async (workspaceId, accountId = null) => {
    if (!workspaceId) return;
    try {
      const res = await authFetch(`${API}/api/workspaces/me/switch-workspace`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ workspace_id: workspaceId, account_id: accountId }),
      });
      if (res.ok) {
        const data = await res.json();
        const nextScope = await fetchScope();
        await fetchAccounts(nextScope?.workspace_id || workspaceId);
        await Promise.all([fetchWorkspaces(), fetchSetupState()]);
        return data;
      }
    } catch {}
    return null;
  }, [fetchAccounts, fetchScope, fetchSetupState, fetchWorkspaces]);

  const value = {
    scope,
    workspaces,
    accounts,
    loading,
    setupState,
    setupLoading,
    switchAccount,
    switchWorkspace,
    refreshScope: fetchScope,
    refreshAccounts: fetchAccounts,
    refreshSetupState: fetchSetupState,
    refreshAll,
  };

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) {
    return {
      scope: null,
      workspaces: [],
      accounts: [],
      loading: true,
      setupState: null,
      setupLoading: true,
      switchAccount: () => {},
      switchWorkspace: () => {},
      refreshScope: () => {},
      refreshAccounts: () => {},
      refreshSetupState: () => {},
      refreshAll: () => {},
    };
  }
  return ctx;
}
